import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, List, ListItem, ListItemText, Button, Stack, Tabs, Tab } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Link as RouterLink } from 'react-router-dom';

const Bookmarks: React.FC = () => {
  const { authenticated, login } = useAuth();
  const [tab, setTab] = useState<'all'|'bill'|'member'|'summary'>('all');
  const [items, setItems] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (t: 'all'|'bill'|'member'|'summary' = tab) => {
    if (!authenticated) { setItems(null); return; }
    try {
      const resp = await apiService.listMyBookmarks(t === 'all' ? undefined : t, 100, 0);
      const arr = Array.isArray(resp.data?.items) ? resp.data.items : [];
      setItems(arr);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load bookmarks');
      setItems([]);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [authenticated, tab]);

  const onTab = (_: any, v: number) => {
    const map: any = {0:'all',1:'bill',2:'member',3:'summary'};
    setTab(map[v] as any);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>My Bookmarks</Typography>
      {!authenticated ? (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="textSecondary">Sign in to see bookmarks.</Typography>
              <Button variant="contained" onClick={login}>Sign In</Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <>
          <Tabs value={{all:0,bill:1,member:2,summary:3}[tab]} onChange={onTab} sx={{ mb: 2 }}>
            <Tab label="All" />
            <Tab label="Bills" />
            <Tab label="Members" />
            <Tab label="Summaries" />
          </Tabs>
          {error && (<Typography color="error" sx={{ mb: 1 }}>{error}</Typography>)}
          {items === null ? (
            <Typography color="textSecondary">Loading…</Typography>
          ) : items.length === 0 ? (
            <Typography color="textSecondary">No bookmarks yet.</Typography>
          ) : (
            <Card>
              <CardContent>
                <List>
                  {items.map((it: any, idx: number) => {
                    if (it.target_type === 'bill' && it.bill) {
                      const b = it.bill;
                      const to = b.id ? `/bills/${b.id}` : undefined;
                      return (
                        <ListItem key={idx} divider component={to ? (RouterLink as any) : 'li'} to={to || ''}>
                          <ListItemText primary={b.title || b.id} secondary={b.latest_action_text || b.latest_action_date || ''} />
                        </ListItem>
                      );
                    }
                    if (it.target_type === 'member' && it.member) {
                      const m = it.member;
                      const to = m.bioguide_id ? `/members/${m.bioguide_id}` : undefined;
                      const sec = [m.party, m.state].filter(Boolean).join(' • ');
                      return (
                        <ListItem key={idx} divider component={to ? (RouterLink as any) : 'li'} to={to || ''}>
                          <ListItemText primary={m.name || m.bioguide_id} secondary={sec} />
                        </ListItem>
                      );
                    }
                    return (
                      <ListItem key={idx} divider>
                        <ListItemText primary={`${it.target_type}:${it.target_id}`} secondary={it.created_at} />
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default Bookmarks;
